import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, Locale } from '@smg-real-estate/geo-libs';
import { defineStore } from 'pinia';

import { fetchAgents } from '@/api/agentMetaApi';
import { DEFAULT_AGENCY_PAGE_LIST_SIZE } from '@/api/customerMetaApi';
import {
  buildFacetQuery,
  emptyAgentSearchFacets,
  toRequestFacets,
  type AgentSearchFacets,
} from '@/helpers/AgentDirectory/agentFacetRouteParams';
import {
  applySelectedLocationsToRoute,
  getLocationGeoId,
  resolveGeoLocations,
} from '@/stores/geoLocationHelpers';
import { LoadState } from '@/stores/types';
import type { AgentProfile } from '@/types/agentProfile';

export interface AgentProfilesState {
  agents: AgentProfile[];
  agentsLoadState: LoadState;
  totalAgentsCount: number;
  agentListCurrentPage: number;
  /* searchedLocations represent locations resolved from the current route */
  searchedLocations: GeoLocation[] | undefined;
  /* selectedLocations represent location autocomplete input state (drives nextPageRoute) */
  selectedLocations: GeoLocation[];
  searchedFacets: AgentSearchFacets;

  /**
   * The guard to avoid race conditions when loading agents.
   */
  agentsRequestGeneration: number;
}

export const useAgentProfilesStore = defineStore('agentProfiles', {
  state: (): AgentProfilesState => ({
    agents: [],
    agentsLoadState: LoadState.UNDEFINED,
    totalAgentsCount: 0,
    agentListCurrentPage: 1,
    searchedLocations: undefined,
    selectedLocations: [],
    searchedFacets: emptyAgentSearchFacets(),
    agentsRequestGeneration: 0,
  }),
  actions: {
    /**
     * Loads the agents for the current page.
     * @param currentPage - The current page number (1-based index)
     */
    async loadAgents(currentPage = 1, requestGeneration?: number) {
      const generation = requestGeneration ?? ++this.agentsRequestGeneration;

      this.agents = [];
      this.agentsLoadState = LoadState.LOADING;
      const geoLocationIds = (this.searchedLocations ?? [])
        .map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id);

      try {
        const agents = await fetchAgents({
          from: (currentPage - 1) * DEFAULT_AGENCY_PAGE_LIST_SIZE,
          geoLocationIds,
          facets: toRequestFacets(this.searchedFacets),
        });

        if (generation !== this.agentsRequestGeneration) {
          // Abort mutating the state if the stale request comes later
          return;
        }

        this.agents = agents.records;
        this.totalAgentsCount = agents.total;
        this.agentsLoadState = LoadState.LOADED;
      } catch (error) {
        if (generation !== this.agentsRequestGeneration) {
          return;
        }

        this.agentsLoadState = LoadState.FAILED;
      }
    },
    beginAgentRouteSync(): number {
      return ++this.agentsRequestGeneration;
    },
    async applyUrlParamsFilters({
      geoLocations,
      locale,
      currentPage,
      requestGeneration,
      resolvedLocations: resolvedLocationsParam,
      facets,
    }: {
      geoLocations: string[];
      locale: HgSupportedLanguage;
      currentPage: number;
      requestGeneration?: number;
      resolvedLocations?: GeoLocation[];
      facets?: AgentSearchFacets;
    }) {
      const generation = requestGeneration ?? ++this.agentsRequestGeneration;

      try {
        const resolvedLocations =
          resolvedLocationsParam ?? (await resolveGeoLocations(geoLocations, locale));

        if (generation !== this.agentsRequestGeneration) {
          return;
        }

        // The route is the source of truth for the agent directory:
        // an empty location param means a Switzerland-wide search.
        this.searchedLocations = resolvedLocations;
        this.selectedLocations = resolvedLocations;
        this.searchedFacets = facets ?? emptyAgentSearchFacets();

        await this.loadAgents(currentPage, generation);

        if (generation !== this.agentsRequestGeneration) {
          return;
        }

        // Set the state later to trigger the FeaturedPlacement component changed after the agents are loaded
        this.$patch({
          agentListCurrentPage: currentPage,
        });
      } catch (error) {
        console.error(error);
      }
    },
    changeSelectedLocations(geoLocations: GeoLocation[]) {
      this.selectedLocations = geoLocations;
    },
    setRouteResolvedLocations(resolvedLocations: GeoLocation[], facets?: AgentSearchFacets) {
      this.searchedLocations = resolvedLocations;
      this.selectedLocations = resolvedLocations;
      if (facets) {
        this.searchedFacets = facets;
      }
    },
  },
  getters: {
    pageCount: (state): number => Math.ceil(state.totalAgentsCount / DEFAULT_AGENCY_PAGE_LIST_SIZE),
    nextPageRoute: (state) => {
      return (page: number, locale: Locale) => {
        const nextRoute: {
          params: { location: string | undefined };
          query: {
            page?: number;
            loc?: string;
            services?: string;
            specializations?: string;
          };
        } = {
          params: { location: undefined },
          query: {
            ...(page > 1 ? { page } : {}),
            ...buildFacetQuery(state.searchedFacets),
          },
        };
        return applySelectedLocationsToRoute(nextRoute, state.selectedLocations, locale);
      };
    },
    searchedGeoIds: (state) =>
      state.searchedLocations
        ?.map((location) => getLocationGeoId(location) ?? '')
        .filter((id) => !!id),
  },
});
